import { useQuery } from "@tanstack/react-query";
import { create, bufferScheduler } from "@yornaath/batshit";
import groupBy from "lodash.groupby";

import fallbackContent from "./../fallback/fallbackContentA.json";

export type GetZones = (context: { queryKey: ZoneKey }) => Promise<unknown>;

export type ZoneParams = {
  pageType: string;
  selectionKey: string;
  zoneId: string;
};

export type ZoneKey = ["zone", ZoneParams];

export type ZoneResponse = {
  order: number;
  [key: string]: unknown;
};

export type FetchPageResponse = {
  pageType: string;
  selectionKey: string;
  zones: ZoneResponse[];
};

export const getZoneKey = (params: ZoneParams): ZoneKey => ["zone", params];

export const createGetZones = (baseUrl = "/api/content", fetcher: typeof fetch = fetch): GetZones => {
  async function fetchPage(pageType: string, selectionKey: string, zoneIds: number[]): Promise<FetchPageResponse> {
    if (!zoneIds.length) {
      return { pageType, selectionKey, zones: [] };
    }
    const params = new URLSearchParams({
      zoneRangeFrom: Math.max(Math.min(...zoneIds), 1).toString(),
      zoneRangeTo: Math.min(Math.max(...zoneIds), 30).toString(),
    });
    switch (pageType) {
      case "pdp":
        params.set("partNumbers", selectionKey);
        break;
      case "plp":
        params.set("categoryId", selectionKey);
        break;
      default:
        params.set("selectionKey", selectionKey);
        break;
    }
    const url = `${baseUrl}/v1/pages/${pageType}?${params}`;
    const res = await fetcher(url);
    if (!res.ok) {
      if (pageType === "hp") {
        return {
          pageType,
          selectionKey,
          zones: fallbackContent.zones ?? [],
        };
      }
      throw new Error("Network response was not ok");
    }
    const zones = await res
      .json()
      .then((data) => data.zones ?? [])
      .catch(() => []);
    return {
      pageType,
      selectionKey,
      zones,
    };
  }

  const zones = create({
    fetcher: (keys: ZoneKey[]) =>
      Promise.allSettled(
        Object.entries(groupBy(keys, ([, k]) => k.pageType)).flatMap(([pageType, pageKeys]) =>
          Object.entries(groupBy(pageKeys, ([, k]) => k.selectionKey)).map(([selectionKey, selectionKeys]) => {
            const zoneIds = selectionKeys
              .map(([, { zoneId }]) => Number.parseInt(zoneId!, 10))
              .filter((zoneId) => !Number.isNaN(zoneId));
            return fetchPage(pageType, selectionKey, zoneIds);
          }),
        ),
      ),
    scheduler: bufferScheduler(5),
    resolver: (results, [, { zoneId, selectionKey, pageType }]): ZoneResponse | undefined => {
      const response = results.find(
        (result): result is PromiseFulfilledResult<FetchPageResponse> =>
          result.status === "fulfilled" &&
          result.value.pageType === pageType &&
          result.value.selectionKey === selectionKey,
      );
      return response?.value.zones.find(({ order }) => order.toString() === zoneId);
    },
  });

  return ({ queryKey }) => zones.fetch(queryKey);
};

export const getZones = createGetZones();

export const useZone = (params: ZoneParams) =>
  useQuery({
    queryKey: getZoneKey(params),
    queryFn: getZones,
    enabled(query) {
      const [, { zoneId, pageType }] = query.queryKey;
      return Boolean(zoneId && pageType);
    },
  });
